// What a person agrees to about messages from Kanfy and where their data is
// processed (docs/ai-management-org/LEGAL_REVIEW.md §6; migration
// 20270580000000). Three things, kept apart because the law treats them apart:
//   - service reminders about their own sign-up, requests and bookings, per
//     channel (on unless they turn them off);
//   - offers and news (marketing), per channel, never ticked for them;
//   - processing by Kanfy's providers outside their country.
// The answers are recorded with the version of the wording they saw, so a
// change of wording is a new version here.
import { supabase } from '@/integrations/supabase/client';

// choices-2026-11b adds the DeepSeek box (D60).
export const CONSENT_WORDING_VERSION = 'choices-2026-11b';

export type ConsentChannel = 'email' | 'whatsapp' | 'sms';

export const CONSENT_CHANNELS: { key: ConsentChannel; label: string }[] = [
  { key: 'email', label: 'by email' },
  { key: 'whatsapp', label: 'by WhatsApp' },
  { key: 'sms', label: 'by SMS' },
];

export interface ConsentChoices {
  transferAbroad: boolean;
  /** DeepSeek, in China, may read my messages to help answer them (D60). Optional, never ticked for anyone. */
  aiChina: boolean;
  serviceReminders: Record<ConsentChannel, boolean>;
  marketing: Record<ConsentChannel, boolean>;
}

/** What the database holds: null where the person was never asked. */
export interface SavedConsents {
  answered: boolean;
  transfer_abroad: boolean | null;
  /** Null until the person is asked about DeepSeek (D60). */
  ai_china?: boolean | null;
  service_reminders: Record<string, boolean | null>;
  marketing: Record<string, boolean>;
}

export const TRANSFER_WORDING =
  'Kanfy’s service providers process my details outside my country, in the United States and the European Union: ' +
  'Supabase (where my account is kept), Anthropic and OpenAI (AI that helps answer support messages and check safety reports), ' +
  'Resend (email) and Meta (WhatsApp).';

export const AI_CHINA_WORDING =
  'DeepSeek, in China, may also read my support messages and safety reports to help answer them, ' +
  'with phone numbers and emails removed, when the other AI services are unavailable (optional).';

export const SERVICE_WORDING =
  'Reminders about things I started: finishing my profile, my requests and my bookings.';

export const MARKETING_WORDING = 'Offers and news from Kanfy (optional).';

const byChannel = (value: boolean): Record<ConsentChannel, boolean> => ({ email: value, whatsapp: value, sms: value });

/** The starting point of a question never answered: reminders on, offers off, nothing agreed abroad. */
export function defaultChoices(): ConsentChoices {
  return { transferAbroad: false, aiChina: false, serviceReminders: byChannel(true), marketing: byChannel(false) };
}

/** Saved answers as choices; a channel never answered reads as off. */
export function choicesFromSaved(saved: SavedConsents | null): ConsentChoices {
  if (!saved?.answered) return defaultChoices();
  const pick = (map: Record<string, boolean | null> | undefined, key: ConsentChannel) => map?.[key] === true;
  return {
    transferAbroad: saved.transfer_abroad === true,
    aiChina: saved.ai_china === true,
    serviceReminders: { email: pick(saved.service_reminders, 'email'), whatsapp: pick(saved.service_reminders, 'whatsapp'), sms: pick(saved.service_reminders, 'sms') },
    marketing: { email: pick(saved.marketing, 'email'), whatsapp: pick(saved.marketing, 'whatsapp'), sms: pick(saved.marketing, 'sms') },
  };
}

/** The shape the database records (set_my_consents, and the sign-up metadata). */
export function choicesPayload(choices: ConsentChoices) {
  return {
    transfer_abroad: choices.transferAbroad,
    // Only with processing abroad: without it no AI reads their data at all.
    ai_china: choices.transferAbroad && choices.aiChina,
    service_reminders: { ...choices.serviceReminders },
    marketing: { ...choices.marketing },
  };
}

/** Sent with the sign-up; recorded when the account is made. */
export function signupConsentsMetadata(choices: ConsentChoices) {
  return { wording_version: CONSENT_WORDING_VERSION, ...choicesPayload(choices) };
}

/** Asked once: never answered, or answered before the DeepSeek box existed (D60). */
export function needsAsking(saved: SavedConsents | null): boolean {
  return !!saved && (!saved.answered || saved.ai_china === null || saved.ai_china === undefined);
}

/** get_my_consents / set_my_consents answer one object, or null when signed out. */
function readSaved(data: unknown): SavedConsents | null {
  return data && typeof data === 'object' && 'answered' in data ? (data as SavedConsents) : null;
}

export async function fetchMyConsents(): Promise<SavedConsents | null> {
  const { data, error } = await supabase.rpc('get_my_consents');
  if (error) throw error;
  return readSaved(data);
}

export async function saveMyConsents(
  choices: ConsentChoices,
  source: 'settings' | 'first_sign_in' | 'onboarding',
): Promise<SavedConsents | null> {
  const { data, error } = await supabase.rpc('set_my_consents', {
    p_choices: choicesPayload(choices),
    p_source: source,
    p_wording_version: CONSENT_WORDING_VERSION,
  });
  if (error) throw error;
  return readSaved(data);
}
